/**
 * Admin sub-role permission matrix
 * DB roles enum: admin, ceo, manager, marketing, support (lowercase)
 * admin = Super Admin
 */
export const ADMIN_ROLES = ['admin', 'ceo', 'manager', 'marketing', 'support'];

export const ROLE_DISPLAY_NAMES = {
  admin: 'Super Admin',
  ceo: 'CEO',
  manager: 'Manager',
  marketing: 'Marketing',
  support: 'Support',
};

export function getRoleDisplayName(role) {
  return ROLE_DISPLAY_NAMES[role?.toLowerCase()] || role || 'Support';
}

/**
 * Hierarchical admin creation: who can add whom.
 * Super Admin can add admin; CEO can add manager; Manager can add marketing officer.
 * Higher roles can also add all roles below them.
 */
const CREATABLE_ADMIN_ROLES = {
  admin: ['admin', 'ceo', 'manager', 'marketing', 'support'],
  ceo: ['manager', 'marketing', 'support'],
  manager: ['marketing', 'support'],
  marketing: [],
  support: [],
};

/** Roles the actor can create or assign when adding/editing admin users */
export function getCreatableAdminRoles(actorRole) {
  const r = (actorRole || '').toLowerCase();
  return CREATABLE_ADMIN_ROLES[r] || [];
}

/** True if actor can create/assign a user with targetRole */
export function canAssignAdminRole(actorRole, targetRole) {
  const creatable = getCreatableAdminRoles(actorRole);
  const target = (targetRole || '').toLowerCase();
  return creatable.includes(target);
}

/**
 * Roles the actor can view in Admin Users list.
 * Manager sees only manager, marketing, support — not admin (Super Admin) or CEO.
 * CEO sees ceo, manager, marketing, support — not admin.
 * Super Admin sees all.
 */
export function getViewableAdminRoles(actorRole) {
  const r = (actorRole || '').toLowerCase();
  const creatable = CREATABLE_ADMIN_ROLES[r] || [];
  const own = r && ADMIN_ROLES.includes(r) ? [r] : [];
  return [...new Set([...creatable, ...own])];
}

/** True if actor can view a user with targetRole in Admin Users */
export function canViewAdminRole(actorRole, targetRole) {
  const viewable = getViewableAdminRoles(actorRole);
  const target = (targetRole || '').toLowerCase();
  return viewable.includes(target);
}

const PERMISSIONS = {
  dashboard: {
    admin: ['read', 'full'],
    ceo: ['read', 'full'],
    manager: ['read', 'full'],
    marketing: ['read', 'kpis'],
    support: ['read', 'kpis'],
  },
  users: {
    admin: ['create', 'read', 'update', 'delete'],
    ceo: ['read', 'approve'],
    manager: ['create', 'read', 'update', 'delete'],
    marketing: ['read'],
    support: ['read', 'update'],
  },
  stores: {
    admin: ['create', 'read', 'update', 'delete'],
    ceo: ['read', 'approve'],
    manager: ['create', 'read', 'update', 'delete'],
    marketing: ['read'],
    support: ['read'],
  },
  products: {
    admin: ['create', 'read', 'update', 'delete'],
    ceo: ['read'],
    manager: ['create', 'read', 'update', 'delete'],
    marketing: ['read'],
    support: ['read'],
  },
  orders: {
    admin: ['create', 'read', 'update', 'delete'],
    ceo: ['read'],
    manager: ['create', 'read', 'update', 'delete'],
    marketing: ['read'],
    support: ['create', 'read', 'update', 'delete'],
  },
  drivers: {
    admin: ['create', 'read', 'update', 'delete'],
    ceo: ['read', 'verify'],
    manager: ['create', 'read', 'update', 'delete'],
    marketing: [],
    support: ['read'],
  },
  requests: {
    admin: ['create', 'read', 'update', 'delete'],
    ceo: ['read'],
    manager: ['create', 'read', 'update', 'delete'],
    marketing: [],
    support: [],
  },
  carts: {
    admin: ['read'],
    ceo: ['read'],
    manager: ['read'],
    marketing: [],
    support: ['read'],
  },
  payments: {
    admin: ['read'],
    ceo: ['read'],
    manager: ['read'],
    marketing: [],
    support: [],
  },
  messages: {
    admin: ['read', 'moderate'],
    ceo: ['read'],
    manager: ['read'],
    marketing: [],
    support: ['read', 'reply'],
  },
  reviews: {
    admin: ['read', 'moderate'],
    ceo: ['read'],
    manager: ['read', 'moderate'],
    marketing: ['read'],
    support: ['read'],
  },
  analytics: {
    admin: ['read'],
    ceo: ['read'],
    manager: ['read'],
    marketing: ['read'],
    support: ['read'],
  },
  adminUsers: {
    admin: ['create', 'read', 'update', 'delete'],
    ceo: ['create', 'read', 'role'],
    manager: ['create', 'read', 'role'],
    marketing: [],
    support: [],
  },
  settings: {
    admin: ['read', 'update'],
    ceo: ['read', 'update'],
    manager: ['read', 'update'],
    marketing: [],
    support: [],
  },
  activity: {
    admin: ['read'],
    ceo: ['read'],
    manager: ['read'],
    marketing: [],
    support: [],
  },
  deliverySettings: {
    admin: ['read', 'update'],
    ceo: ['read'],
    manager: ['read', 'update'],
    marketing: [],
    support: [],
  },
  deliveryRatings: {
    admin: ['read', 'update', 'delete'],
    ceo: ['read'],
    manager: ['read', 'update'],
    marketing: [],
    support: ['read', 'update'],
  },
  promotions: {
    admin: ['create', 'read', 'update', 'delete', 'approve'],
    ceo: ['read', 'approve'],
    manager: ['create', 'read', 'update', 'delete', 'approve'],
    marketing: ['create', 'read', 'update'],
    support: ['read'],
  },
  sellerSms: {
    admin: ['read', 'update'],
    ceo: ['read'],
    manager: ['read', 'update'],
    marketing: [],
    support: [],
  },
  payouts: {
    admin: ['read', 'update'],
    ceo: ['read', 'update'],
    manager: ['read', 'update'],
    marketing: [],
    support: [],
  },
  finance: {
    admin: ['read', 'update'],
    ceo: ['read', 'update'],
    manager: ['read'],
    marketing: [],
    support: [],
  },
  escrow: {
    admin: ['read', 'update'],
    ceo: ['read', 'update'],
    manager: ['read', 'update'],
    marketing: [],
    support: [],
  },
  blockedPayouts: {
    admin: ['read', 'update'],
    ceo: ['read', 'update'],
    manager: ['read'],
    marketing: [],
    support: [],
  },
  deliveryClaims: {
    admin: ['read'],
    ceo: ['read'],
    manager: ['read'],
    marketing: [],
    support: ['read'],
  },
  appVersion: {
    admin: ['read', 'update'],
    ceo: ['read', 'update'],
    manager: ['read'],
    marketing: [],
    support: [],
  },
};

export function canAccess(role, section) {
  const r = (role || '').toLowerCase();
  if (!r || !ADMIN_ROLES.includes(r)) return false;
  const perms = PERMISSIONS[section]?.[r];
  return perms && perms.length > 0;
}

export function can(role, action, section) {
  const r = (role || '').toLowerCase();
  if (!r || !ADMIN_ROLES.includes(r)) return false;
  const perms = PERMISSIONS[section]?.[r] || [];
  if (perms.includes('full')) return true;
  return perms.includes(action);
}
